import { UUID } from "../../../utils/typeDefinitions";
import IBimVisualizerApiProvider from "../IBimVisualizerApiProvider";
import IAppDataProvider from "./IAppDataProvider";
import { ConfigurationState } from "../../../store/configurationState/interfaces";
import ProjectDocument from "../../../model/document/ProjectDocument";
import IDocumentProvider from "../../documentProvider/IDocumentProvider";
import VisualizerApiStoreyDocumentProvider from "../../documentProvider/ApiStoreyDocumentProvider";
import SceneObjectData from "../../../model/sceneObjectData/SceneObjectData";
import { IModelResponse } from "../IModelResponse";
import IContextDocument from "../../../model/document/IContextDocument";
import User from "../../../model/User";
import { RoomNoteSaveResult } from "../../coreApi/RoomNoteSaveResult";
import ICoreRoom from "../../../interfaces/ICoreRoom";
import { RoomNoteSaveErrorEnum } from "../../../enums/RoomNoteSaveErrorEnum";

abstract class AppDataProvider implements IAppDataProvider {
    apiProvider: IBimVisualizerApiProvider; 
    #docProvider: IDocumentProvider;

    constructor(apiProvider: IBimVisualizerApiProvider, docProvider: IDocumentProvider) {
        this.apiProvider = apiProvider;
        this.#docProvider = docProvider;
    }

    // Load spatial structure model
    abstract loadRootProduct() : Promise<IModelResponse | null>;
    abstract getCurrentUser(): Promise<User | null>;

    loadDocuments = async () => {
        const result = await this.#docProvider.getContextDocuments();
        return result;
    }

    loadDocument(documentUuid: UUID) {
        return this.#docProvider.getDocument(documentUuid);
    }

    updateConfiguration(currentConfiguration: ConfigurationState) {
        this.apiProvider.configuration = currentConfiguration;
    }

    createInitialDocument = async (): Promise<IContextDocument> => {
        return await this.#docProvider.createInitialDocument();
    }

    async saveDocument(doc: IContextDocument): Promise<boolean> {
        return await this.#docProvider.saveDocument(doc);
    }

    async addSceneObject(documentUuid: UUID, sceneObjectData: SceneObjectData): Promise<SceneObjectData | null> {
        return await this.#docProvider.addSceneObject(documentUuid, sceneObjectData);
    }

    async updateSceneObject(sceneObjectUuid: UUID, sceneObjectData: SceneObjectData): Promise<SceneObjectData | null> {
        return await this.#docProvider.updateSceneObject(sceneObjectUuid, sceneObjectData);
    }

    async removeSceneObject(sceneObjectUuid: UUID): Promise<boolean> {
        return await this.#docProvider.removeSceneObject(sceneObjectUuid);
    }

    async uploadChecklistItemImage(sceneObjectUuid: UUID, image: Blob, filename: string): Promise<string | null> {
        return await this.apiProvider.uploadChecklistItemImage(sceneObjectUuid, image, filename);
    }

    // Room notes need the CoreAPI project and apartment context, so only GbContextAppDataProvider supports them
    async canEditRoomNotes(): Promise<boolean> {
        return false;
    }

    async loadRoomNotes(): Promise<ICoreRoom[]> {
        return [];
    }

    async updateRoomNote(roomId: number, notes: string | null): Promise<RoomNoteSaveResult> {
        return { ok: false, error: RoomNoteSaveErrorEnum.Forbidden };
    }
    
}

export default AppDataProvider;
